import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { CheckCircle2, XCircle } from 'lucide-react'

import { evalKeys } from '@/lib/queryKeys'
import { fetchEvalRunAssertionResults } from '../api'
import { getAssertionStats } from '../evalRunPresentation'
import type { RelayEvalRun } from '../types'

const ASSERTION_POLL_INTERVAL_MS = 4000
// Judging is async (Inngest). Poll briefly after a run completes so a result
// that lands mid-session appears live, then stop — old runs never poll.
const ASSERTION_POLL_WINDOW_MS = 90_000

interface EvalRunAssertionStatsCellProps {
  run: RelayEvalRun
  caseHasAssertions: boolean
}

function endedMsAgo(endedAt: string | null | undefined): number {
  if (!endedAt) return Number.POSITIVE_INFINITY
  const elapsed = Date.now() - new Date(endedAt).getTime()
  return Number.isNaN(elapsed) ? Number.POSITIVE_INFINITY : elapsed
}

export function EvalRunAssertionStatsCell({
  run,
  caseHasAssertions,
}: EvalRunAssertionStatsCellProps) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const isCompleted = run.status === 'completed'

  const resultsQuery = useQuery({
    queryKey: evalKeys.runAssertionResults(organization?.id, run.id),
    enabled: caseHasAssertions && isCompleted,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchEvalRunAssertionResults(token, run.id)
    },
    refetchInterval: (query) => {
      const items = query.state.data?.items
      if (items && items.length > 0) return false
      if (endedMsAgo(run.ended_at) > ASSERTION_POLL_WINDOW_MS) return false
      return ASSERTION_POLL_INTERVAL_MS
    },
    staleTime: 5000,
    refetchOnWindowFocus: false,
  })

  // No verdict is possible unless the case has assertions and the run finished.
  if (!caseHasAssertions || !isCompleted) {
    return <span className="text-neutral-low-contrast">—</span>
  }

  if (resultsQuery.isError) {
    return <span className="text-error-solid">Unavailable</span>
  }

  const results = resultsQuery.data?.items ?? []
  if (results.length === 0) {
    const stillJudging =
      resultsQuery.isLoading ||
      endedMsAgo(run.ended_at) <= ASSERTION_POLL_WINDOW_MS
    return (
      <span className="text-neutral-low-contrast">
        {stillJudging ? 'Judging…' : '—'}
      </span>
    )
  }

  const { total, passed, failed } = getAssertionStats(results)
  const inconclusive = total - passed - failed

  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-full bg-success-ui-bg px-2 py-0.5 text-xs font-semibold text-success-solid">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {passed}
      </span>
      {failed > 0 ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-error-ui-bg px-2 py-0.5 text-xs font-semibold text-error-solid">
          <XCircle className="h-3.5 w-3.5" />
          {failed}
        </span>
      ) : null}
      {inconclusive > 0 ? (
        <span
          title="Inconclusive"
          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-neutral-low-contrast">
          {inconclusive}
        </span>
      ) : null}
    </div>
  )
}
